<template>
  <div class="youtube-embed" :class="{'is-lazy': lazy && !loaded}" :style="containerStyle">
    <!-- Poster + play button (shown when lazy and not loaded) -->
    <button
      v-if="lazy && !loaded"
      @click="load"
      class="yt-poster"
      :aria-label="`Play video: ${videoId}`"
      type="button"
    >
      <img :src="posterUrl" :alt="posterAlt" class="yt-thumbnail" />
      <span class="yt-playbtn" aria-hidden="true">▶</span>
    </button>

    <!-- Iframe (either loaded immediately or after user click) -->
    <div v-show="loaded" class="yt-frame-wrapper">
      <!-- If useApi is enabled we either let YT API create the player or insert iframe -->
      <div v-if="useApi" :id="playerId"></div>
      <iframe
        v-else
        :src="embedSrc"
        ref="iframe"
        :title="iframeTitle"
        frameborder="0"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
        loading="lazy"
        class="yt-iframe"
      ></iframe>
    </div>
  </div>
</template>

<script>
/*
  YouTubeEmbed.vue - Vue 2 component
  Props:
    - videoId (String, required) : YouTube video id (e.g. "dQw4w9WgXcQ")
    - autoplay (Boolean) : autoplay on load (note browsers restrict autoplay)
    - start (Number) : seconds to start from
    - controls (Boolean) : show controls
    - loop (Boolean) : loop playback (requires playlist param when true)
    - modestBranding (Boolean) : modest branding
    - rel (Boolean) : show related videos (true = show, false = no)
    - privacy (Boolean) : use youtube-nocookie.com (privacy-enhanced)
    - lazy (Boolean) : lazy load poster + click to load (default true)
    - useApi (Boolean) : load YouTube IFrame API and create player (gives programmatic control)
    - aspectRatio (String) : CSS aspect ratio like "16/9" or "4/3"
*/
export default {
  name: "YouTubeEmbed",
  props: {
    videoId: { type: String, required: true },
    autoplay: { type: Boolean, default: false },
    start: { type: Number, default: 0 },
    controls: { type: Boolean, default: true },
    loop: { type: Boolean, default: false },
    modestBranding: { type: Boolean, default: true },
    rel: { type: Boolean, default: false },
    privacy: { type: Boolean, default: true },
    lazy: { type: Boolean, default: true },
    useApi: { type: Boolean, default: false },
    aspectRatio: { type: String, default: "16/9" },
    // optional props to expose styling
    width: { type: [String, Number], default: "100%" },
    height: { type: [String, Number], default: "100%" },
  },
  data() {
    return {
      loaded: !this.lazy, // if not lazy, iframe loads immediately
      player: null,
      playerId: `yt-player-${Math.random().toString(36).slice(2, 9)}`,
    };
  },
  computed: {
    domain() {
      return this.privacy ? "https://www.youtube-nocookie.com" : "https://www.youtube.com";
    },
    embedSrc() {
      const params = new URLSearchParams();
      params.set("enablejsapi", this.useApi ? 1 : 0);
      params.set("rel", this.rel ? 1 : 0);
      params.set("controls", this.controls ? 1 : 0);
      params.set("modestbranding", this.modestBranding ? 1 : 0);
      if (this.autoplay) params.set("autoplay", 1);
      if (this.start) params.set("start", Math.max(0, Math.floor(this.start)));
      if (this.loop) {
        params.set("loop", 1);
        // loop requires playlist param equal to videoId
        params.set("playlist", this.videoId);
      }
      // always enable modest accelerated loading
      return `${this.domain}/embed/${this.videoId}?${params.toString()}`;
    },
    posterUrl() {
      // high quality default poster; may fall back if not available
      return `https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg`;
    },
    posterAlt() {
      return `Poster for video ${this.videoId}`;
    },
    iframeTitle() {
      return `YouTube video player: ${this.videoId}`;
    },
    containerStyle() {
      // using CSS aspect-ratio via padding trick if browser doesn't support aspect-ratio
      const [w, h] = this.aspectRatio.split("/").map(Number);
      const padding = (h / w) * 100;
      return {
        width: typeof this.width === "number" ? `${this.width}px` : this.width,
        height: typeof this.height === "number" ? `${this.height}px` : this.height,
        "--yt-aspect-padding": `${padding}%`,
      };
    },
  },
  methods: {
    load() {
      if (this.loaded) return;
      this.loaded = true;
      this.$nextTick(() => {
        if (this.useApi) this.initYouTubeAPI();
      });
    },
    initYouTubeAPI() {
      // Create script if not present
      if (window.YT && window.YT.Player) {
        this.createPlayer();
        return;
      }
      if (document.getElementById("youtube-iframe-api")) {
        // API will call onYouTubeIframeAPIReady
        window.onYouTubeIframeAPIReady = () => this.createPlayer();
        return;
      }
      const tag = document.createElement("script");
      tag.id = "youtube-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      window.onYouTubeIframeAPIReady = () => this.createPlayer();
      document.head.appendChild(tag);
    },
    createPlayer() {
      if (!window.YT || !window.YT.Player) {
        // Shouldn't happen but guard
        console.warn("YouTube API not available");
        return;
      }
      this.player = new window.YT.Player(this.playerId, {
        height: "100%",
        width: "100%",
        videoId: this.videoId,
        playerVars: {
          autoplay: this.autoplay ? 1 : 0,
          controls: this.controls ? 1 : 0,
          modestbranding: this.modestBranding ? 1 : 0,
          rel: this.rel ? 1 : 0,
          start: this.start || 0,
          loop: this.loop ? 1 : 0,
          playlist: this.loop ? this.videoId : undefined,
        },
        events: {
          onReady: (e) => this.$emit("ready", e),
          onStateChange: (e) => this.$emit("state-change", e),
          onError: (e) => this.$emit("error", e),
        },
      });
    },
    // helper methods when using API
    apiPlay() {
      if (this.player && this.player.playVideo) this.player.playVideo();
    },
    apiPause() {
      if (this.player && this.player.pauseVideo) this.player.pauseVideo();
    },
    apiSeek(seconds) {
      if (this.player && this.player.seekTo) this.player.seekTo(seconds, true);
    },
  },
  mounted() {
    if (!this.lazy) {
      // if not lazy and useApi, initialize API right away
      if (this.useApi) this.initYouTubeAPI();
    }
  },
  beforeDestroy() {
    if (this.player && this.player.destroy) {
      this.player.destroy();
      this.player = null;
    }
  },
};
</script>

<style scoped>
.youtube-embed {
  position: relative;
  display: block;
  max-width: 100%;
  background: #000;
  overflow: hidden;
}
/* aspect ratio using padding trick */
.youtube-embed .yt-frame-wrapper,
.youtube-embed .yt-poster {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}
.youtube-embed[style] {
  /* fallback: use CSS variable computed in containerStyle */
  padding-top: var(--yt-aspect-padding, 56.25%);
  position: relative;
  height: 0;
}
.youtube-embed .yt-frame-wrapper,
.youtube-embed .yt-poster {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Poster button */
.yt-poster {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-block;
  line-height: 0;
}
.yt-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: brightness(0.6);
}
/* Play button */
.yt-playbtn {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 48px;
  color: white;
  opacity: 0.95;
  text-shadow: 0 2px 10px rgba(0,0,0,0.6);
  pointer-events: none;
}

/* Iframe styling */
.yt-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
</style>
